import React, { useState } from 'react';
import { Loader2, Clock, ChevronRight, Trash2, Info, Undo2, Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { CARE_VOCAB, sanitizeText, NAME_MAX_LENGTH } from './plantUtils';
import { useLanguage } from '../../contexts/LanguageContext';

const CARE_TEXT_MAX = 80;
// Bloom_tips gets a bigger cap since a helpful tip needs more room than a
// one-line care attribute.
const BLOOM_TIPS_MAX = 250;

const TEXT_CARE_FIELDS = [
  { id: 'family', labelKey: 'family', placeholder: 'e.g., Araceae, Gesneriaceae' },
  { id: 'plant_type', labelKey: 'plantType', placeholder: 'e.g., Herbaceous perennial, Succulent' },
  { id: 'watering', labelKey: 'watering', placeholder: 'e.g., Allow soil to dry between waterings' },
  { id: 'light', labelKey: 'light', placeholder: 'e.g., Bright indirect light' },
  { id: 'humidity', labelKey: 'humidity', placeholder: 'e.g., Average household humidity' },
  { id: 'temperature', labelKey: 'temperature', placeholder: 'e.g., 65-80°F / 18-27°C' },
  { id: 'bloom_season', labelKey: 'bloomSeason', placeholder: 'e.g., Late winter (Feb-Mar)' },
  { id: 'bloom_duration', labelKey: 'bloomDuration', placeholder: 'e.g., 2-3 weeks per bloom' },
];

const DROPDOWN_CARE_FIELDS = [
  { id: 'toxicity', labelKey: 'toxicity' },
  { id: 'environment', labelKey: 'environment' },
  { id: 'growth_rate', labelKey: 'growthRate' },
  { id: 'blooms_indoors', labelKey: 'bloomsIndoors' },
];

// Bloom tips is free-text but longer than the standard 80-char care fields
// so it gets its own row + wider maxLength.
const LONG_TEXT_CARE_FIELDS = [
  { id: 'bloom_tips', labelKey: 'bloomTips', placeholder: 'e.g., Cool winter rest at 55°F triggers spring blooms', maxLength: BLOOM_TIPS_MAX },
];

const handleCareTextChange = (fieldId, value, plantData, setPlantData, maxLength = CARE_TEXT_MAX) => {
  const sanitized = sanitizeText(value, maxLength);
  setPlantData({
    ...plantData,
    care_details: { ...plantData.care_details, [fieldId]: sanitized }
  });
};

const handleNameChange = (field, value, plantData, setPlantData) => {
  setPlantData({ ...plantData, [field]: sanitizeText(value, NAME_MAX_LENGTH) });
};

export const DetailsStep = ({ plantData, setPlantData, onClose, handleSave, handleCompleteLater, saving, batchMode, onRemovePhoto, photoDupMatch, editPlant, onDelete, onAiLookup, lookingUp, identifyFailed = false, onRetryIdentify, identifying = false }) => {
  const { t } = useLanguage();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleUndo = () => {
    if (!editPlant) return;
    setPlantData({
      common_name: editPlant.common_name || '',
      scientific_name: editPlant.scientific_name || '',
      care_details: editPlant.care_details || {
        watering: '', light: '', humidity: '', temperature: '', toxicity: '', toxicity_notes: '', growth_rate: '', family: '', plant_type: '', environment: '', bloom_season: '', bloom_duration: '', blooms_indoors: '', bloom_tips: ''
      },
    });
  };

  return (
    <div className="space-y-4">
      {photoDupMatch && !editPlant && (
        <div
          className="flex items-start gap-2 rounded-lg px-3 py-2.5"
          style={{ backgroundColor: 'hsl(40, 90%, 95%)', border: '1px solid hsl(40, 70%, 80%)' }}
          data-testid="photo-duplicate-warning"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(35, 80%, 45%)' }} aria-hidden="true" />
          <p className="text-xs leading-relaxed" style={{ color: 'hsl(35, 45%, 28%)' }}>
            {photoDupMatch.where === 'library'
              ? (t('photoDupLibrary') || 'This exact photo is already in your library as "{name}". You can add it anyway or remove it.').replace('{name}', photoDupMatch.name || t('anotherPlant') || 'another plant')
              : (t('photoDupBatch') || 'You already added this photo earlier in this batch. You can add it anyway or remove it.')}
          </p>
        </div>
      )}
      {editPlant && (
        <Button
          onClick={handleUndo}
          variant="outline"
          size="sm"
          className="gap-1.5"
          style={{ borderColor: 'hsl(210, 30%, 75%)', color: 'hsl(210, 30%, 40%)' }}
          data-testid="undo-changes-button"
        >
          <Undo2 className="w-3.5 h-3.5" />
          Undo changes
        </Button>
      )}

      {/* AI Retry banner — surfaces only after a failed AI identify on a
          NEW-plant flow (not while editing). Lets the user re-run the AI
          on the crops they already made, saving them from starting over. */}
      {identifyFailed && !editPlant && onRetryIdentify && (
        <div
          className="flex items-center gap-3 rounded-lg px-3 py-2.5"
          style={{ backgroundColor: 'hsl(21, 90%, 96%)', border: '1px solid hsl(21, 70%, 85%)' }}
          data-testid="retry-identify-banner"
        >
          <Info className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(21, 70%, 45%)' }} />
          <p className="text-xs flex-1" style={{ color: 'hsl(21, 40%, 30%)' }}>
            {t('retryIdentifyBlurb') || "AI couldn't identify your plant. Try again with the same photos?"}
          </p>
          <Button
            type="button"
            size="sm"
            onClick={onRetryIdentify}
            disabled={identifying || saving}
            className="gap-1.5 flex-shrink-0"
            style={{ backgroundColor: 'hsl(21, 70%, 42%)', color: 'white' }}
            data-testid="retry-identify-button"
          >
            {identifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {identifying ? (t('retryingIdentify') || 'Retrying…') : (t('retryIdentify') || 'Try AI again')}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="common_name">{t('commonName')} *</Label>
        <Input id="common_name" value={plantData.common_name}
          onChange={(e) => handleNameChange('common_name', e.target.value, plantData, setPlantData)}
          placeholder="e.g., Monstera Deliciosa"
          maxLength={NAME_MAX_LENGTH}
          aria-required="true"
          data-testid="common-name-input" />
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{(plantData.common_name || '').length}/{NAME_MAX_LENGTH}</p>
          {onAiLookup && (
            <button
              onClick={() => onAiLookup(plantData.common_name)}
              disabled={lookingUp || !(plantData.common_name || '').trim()}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-all hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                backgroundColor: lookingUp ? 'hsl(143, 28%, 92%)' : 'hsl(143, 28%, 94%)',
                color: 'hsl(143, 28%, 25%)',
                border: '1px solid hsl(143, 28%, 80%)',
              }}
              data-testid="ai-autofill-button"
            >
              {lookingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {t('aiAutoFill')}
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="scientific_name">{t('scientificName')}</Label>
        <Input id="scientific_name" value={plantData.scientific_name}
          onChange={(e) => handleNameChange('scientific_name', e.target.value, plantData, setPlantData)}
          placeholder="e.g., Monstera deliciosa" className="font-mono"
          maxLength={NAME_MAX_LENGTH}
          data-testid="scientific-name-input" />
        <p className="text-xs text-muted-foreground">{(plantData.scientific_name || '').length}/{NAME_MAX_LENGTH}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {TEXT_CARE_FIELDS.map(field => (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{t(field.labelKey)}</Label>
            <Input id={field.id} value={plantData.care_details[field.id] || ''}
              onChange={(e) => handleCareTextChange(field.id, e.target.value, plantData, setPlantData)}
              placeholder={field.placeholder}
              maxLength={CARE_TEXT_MAX}
              data-testid={`care-input-${field.id}`} />
          </div>
        ))}

        {DROPDOWN_CARE_FIELDS.map(field => {
          const options = CARE_VOCAB[field.id] || [];
          const currentValue = plantData.care_details[field.id] || '';
          return (
            <div key={field.id} className="space-y-2">
              <Label htmlFor={field.id}>{t(field.labelKey)}</Label>
              <Select
                value={currentValue}
                onValueChange={(val) => setPlantData({
                  ...plantData,
                  care_details: { ...plantData.care_details, [field.id]: val }
                })}
              >
                <SelectTrigger id={field.id} data-testid={`care-select-${field.id}`}>
                  <SelectValue placeholder={`Select ${t(field.labelKey).toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {options.map(opt => (
                    <SelectItem key={opt} value={opt} data-testid={`care-option-${field.id}-${opt.replace(/\s+/g, '-')}`}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      {/* Long-form care text fields (e.g. bloom_tips) get their own row so
          the ~250-char tip has room to breathe rather than being crammed
          into a 2-col grid cell. */}
      {LONG_TEXT_CARE_FIELDS.map(field => (
        <div key={field.id} className="space-y-2">
          <Label htmlFor={field.id}>{t(field.labelKey)}</Label>
          <Input
            id={field.id}
            value={plantData.care_details[field.id] || ''}
            onChange={(e) => handleCareTextChange(field.id, e.target.value, plantData, setPlantData, field.maxLength || CARE_TEXT_MAX)}
            placeholder={field.placeholder}
            maxLength={field.maxLength || CARE_TEXT_MAX}
            data-testid={`care-input-${field.id}`}
          />
          <p className="text-xs text-muted-foreground">{(plantData.care_details[field.id] || '').length}/{field.maxLength || CARE_TEXT_MAX}</p>
        </div>
      ))}

      {/* AI Disclaimer */}
      <div className="flex items-start gap-2 rounded-lg px-3 py-2" style={{ backgroundColor: 'hsl(40, 60%, 96%)', border: '1px solid hsl(40, 40%, 88%)' }} data-testid="ai-disclaimer">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(40, 50%, 45%)' }} />
        <p className="text-xs leading-relaxed" style={{ color: 'hsl(40, 30%, 30%)' }}>
          {t('aiDisclaimer')}
        </p>
      </div>

      <div className="flex gap-2 pt-4 flex-wrap">
        {editPlant && onDelete && (
          <Button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving}
            variant="outline"
            style={{ borderColor: 'hsl(0, 70%, 85%)', color: 'hsl(0, 70%, 50%)' }}
            data-testid="delete-plant-from-edit-button"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {t('delete')}
          </Button>
        )}
        <Button
          onClick={handleCompleteLater}
          disabled={saving}
          variant="outline"
          style={{ borderColor: 'hsl(21, 70%, 75%)', color: 'hsl(21, 70%, 45%)' }}
          data-testid="complete-later-button"
        >
          <Clock className="w-4 h-4 mr-1" />
          {saving ? t('saving') : t('completeLater')}
        </Button>
        <Button onClick={onClose} variant="outline" data-testid="cancel-button">
          {t('cancel')}
        </Button>
        {batchMode && !editPlant && onRemovePhoto && (
          <Button
            onClick={onRemovePhoto}
            disabled={saving}
            variant="outline"
            style={{ borderColor: 'hsl(0, 70%, 85%)', color: 'hsl(0, 70%, 50%)' }}
            data-testid="remove-photo-button"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {t('removeThisPhoto') || 'Remove this photo'}
          </Button>
        )}
        <Button
          onClick={handleSave} disabled={saving} className="flex-1"
          style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'hsl(115, 56%, 96%)' }}
          data-testid="save-plant-button"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('saving')}</>
          ) : batchMode ? (
            <>{t('saveAndNext')}<ChevronRight className="w-4 h-4 ml-1" /></>
          ) : (
            t('savePlant')
          )}
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent style={{ backgroundColor: 'white' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'hsl(143, 28%, 25%)', fontSize: '1.5rem' }}>{t('deleteThisPlant')}</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '1rem' }}>
              {t('deleteConfirmLocal')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowDeleteConfirm(false); onDelete(editPlant.plant_id); }} style={{ backgroundColor: 'hsl(0, 70%, 50%)', color: 'white' }}>
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
